import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Chip,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  FormLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  AutoAwesome as AutoAwesomeIcon,
  Psychology as PsychologyIcon,
  RocketLaunch as RocketLaunchIcon,
  PhotoCamera as CameraIcon,
  Collections as CollectionsIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { backendApiService } from '../../services/backendApiService';
import { QuerySnapshotsViewer } from './QuerySnapshotsViewer';

interface ExpandedQuery {
  query: string;
  type: string;
  userIntent: string;
  reasoning: string;
}

interface GenerationDetails {
  targetQueryCount: number;
  reasoningForCount: string;
}

export const QueryExpander: React.FC = () => {
  const [query, setQuery] = useState('');
  const [mode, setMode] = useState<'simple' | 'complex'>('simple');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedQueries, setExpandedQueries] = useState<ExpandedQuery[]>([]);
  const [generationDetails, setGenerationDetails] = useState<GenerationDetails | null>(null);
  const [capturingQuery, setCapturingQuery] = useState<string | null>(null);
  const [showSnapshots, setShowSnapshots] = useState(false);

  const handleExpand = async () => {
    if (!query.trim()) {
      setError('Please enter a query to expand');
      return;
    }

    setLoading(true);
    setError(null);
    setExpandedQueries([]);
    setGenerationDetails(null);

    try {
      const result = await backendApiService.post('/api/gemini/expand-query', {
        query,
        mode
      });

      if (result.success && result.data) {
        setExpandedQueries(result.data.expandedQueries || []);
        setGenerationDetails(result.data.generationDetails || null);
      } else {
        setError(result.error || 'Failed to expand query');
      }
    } catch (err: any) {
      console.error('Query expansion error:', err);
      setError(err.response?.data?.error || 'An error occurred during query expansion');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleExpand();
    }
  };

  const getTypeColor = (type: string): string => {
    const colors: Record<string, string> = {
      reformulation: '#4285f4',
      'related query': '#34a853',
      'implicit query': '#fbbc04',
      'comparative query': '#ea4335',
      'entity expansion': '#673ab7',
      'personalized query': '#ff6f00',
    };
    return colors[type.toLowerCase()] || '#666';
  };

  const getTypeIcon = (type: string) => {
    switch (type.toLowerCase()) {
      case 'reformulation':
      case 'related query':
        return <AutoAwesomeIcon sx={{ fontSize: 16 }} />;
      case 'implicit query':
      case 'comparative query':
        return <PsychologyIcon sx={{ fontSize: 16 }} />;
      default:
        return <RocketLaunchIcon sx={{ fontSize: 16 }} />;
    }
  };

  // Group queries by type
  const groupedQueries = expandedQueries.reduce((acc, query) => {
    const type = query.type.toLowerCase();
    if (!acc[type]) {
      acc[type] = [];
    }
    acc[type].push(query);
    return acc;
  }, {} as Record<string, ExpandedQuery[]>);

  const handleCaptureQuery = async (queryText: string) => {
    setCapturingQuery(queryText);
    
    try {
      // Use a special marker to indicate this is a query-based capture (not tied to a URL)
      const result = await backendApiService.post('/api/ai-screenshots/capture-query', {
        query: queryText,
        source: 'query_expander',
        country: 'UK', // Default to UK for English queries
        priority: 'immediate'
      });
      
      if (result.success) {
        // Show success notification
        setError(null);
        alert(`Screenshot queued for: "${queryText}"\n\nIt will be captured in the background.`);
      } else {
        setError(`Failed to capture screenshot for query: ${result.error}`);
      }
    } catch (err: any) {
      console.error('Screenshot capture error:', err);
      setError(err.response?.data?.error || 'Failed to capture screenshot');
    } finally {
      setCapturingQuery(null);
    }
  };

  const downloadCSV = () => {
    const headers = ['Query', 'Type', 'User Intent', 'Reasoning'];
    const rows = expandedQueries.map(q => [
      q.query,
      q.type,
      q.userIntent,
      q.reasoning
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell.replace(/"/g, '""')}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `query-expansion-${query.replace(/[^a-z0-9]/gi, '-').toLowerCase()}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <Box>
      {/* Input Section */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AutoAwesomeIcon />
          Query Expansion Configuration
        </Typography>
        
        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            label="Enter your query"
            placeholder="What's the best electric SUV for driving up mt rainier?"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyPress={handleKeyPress}
            variant="outlined"
            multiline
            rows={2}
            sx={{ mb: 2 }}
          />
          
          <FormControl component="fieldset">
            <FormLabel component="legend">Search Mode</FormLabel>
            <RadioGroup
              row
              value={mode}
              onChange={(e) => setMode(e.target.value as 'simple' | 'complex')}
            >
              <FormControlLabel 
                value="simple" 
                control={<Radio />} 
                label={
                  <Box>
                    <Typography variant="body2" fontWeight={500}>
                      AI Overview (simple)
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      ~10-15 queries for basic coverage
                    </Typography>
                  </Box>
                }
              />
              <FormControlLabel 
                value="complex" 
                control={<Radio />} 
                label={
                  <Box>
                    <Typography variant="body2" fontWeight={500}>
                      AI Mode (complex)
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      ~20-30 queries for comprehensive analysis
                    </Typography>
                  </Box>
                }
              />
            </RadioGroup>
          </FormControl>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Button
            variant="contained"
            onClick={handleExpand}
            disabled={loading || !query.trim()}
            startIcon={loading ? <CircularProgress size={20} /> : <RocketLaunchIcon />}
            sx={{ minWidth: 150 }}
          >
            {loading ? 'Expanding...' : 'Run Fan-Out'}
          </Button>
          
          {expandedQueries.length > 0 && (
            <>
              <Button
                variant="outlined"
                onClick={downloadCSV}
                sx={{ minWidth: 150 }}
              >
                Download CSV
              </Button>
              <Button
                variant="outlined"
                onClick={() => setShowSnapshots(true)}
                startIcon={<CollectionsIcon />}
                sx={{ minWidth: 150 }}
              >
                View Snapshots
              </Button>
            </>
          )}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </Paper>

      {/* Generation Details */}
      {generationDetails && (
        <Paper sx={{ p: 3, mb: 4, bgcolor: 'grey.50' }}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PsychologyIcon />
            Model's Query Generation Plan
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="body2">
              <strong>Target Queries:</strong> {generationDetails.targetQueryCount}
            </Typography>
            <Typography variant="body2">
              <strong>Actual Generated:</strong> {expandedQueries.length}
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              <strong>Model's Reasoning:</strong> <em>{generationDetails.reasoningForCount}</em>
            </Typography>
          </Box>
        </Paper>
      )}

      {/* Results */}
      {expandedQueries.length > 0 && (
        <Box>
          <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
            Expanded Queries ({expandedQueries.length} total)
          </Typography>

          {/* Display by type */}
          {Object.entries(groupedQueries).map(([type, queries]) => (
            <Accordion key={type} defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                  <Chip
                    icon={getTypeIcon(type)}
                    label={type.charAt(0).toUpperCase() + type.slice(1)}
                    size="small"
                    sx={{ 
                      bgcolor: getTypeColor(type),
                      color: 'white',
                      fontWeight: 500
                    }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {queries.length} queries
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {queries.map((query, index) => (
                    <Card key={index} variant="outlined">
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body1" fontWeight={500} gutterBottom>
                              {query.query}
                            </Typography>
                            <Divider sx={{ my: 1 }} />
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                              <strong>Intent:</strong> {query.userIntent}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              <strong>Reasoning:</strong> {query.reasoning}
                            </Typography>
                          </Box>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={capturingQuery === query.query ? <CircularProgress size={16} /> : <CameraIcon />}
                            onClick={() => handleCaptureQuery(query.query)}
                            disabled={capturingQuery === query.query}
                            sx={{ ml: 2, minWidth: 100 }}
                          >
                            {capturingQuery === query.query ? 'Capturing...' : 'Capture'}
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      )}

      {/* Query Snapshots Dialog */}
      <Dialog 
        open={showSnapshots} 
        onClose={() => setShowSnapshots(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Query AI Overview Snapshots</Typography>
            <IconButton onClick={() => setShowSnapshots(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <QuerySnapshotsViewer />
        </DialogContent>
      </Dialog>
    </Box>
  );
};